import CountryInput from '@/components/FormItems/CountryInput';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import { ROW_GUTTER, SupportedDateFormat, SupportedTimeFormat, VALIDATE_REQUIRED, applyCompanyDateFormat, toDisplayDateFormat, toServerDateFormat } from '@/config';
import { ActionCasseType, BlobStorageContainerType, ShipmentInsuranceSettingType } from '@/constants';
import { getSelectionFromArray } from '@/helpers/form';
import { apiSdk } from '@/httpClient';
import { Button, Card, Checkbox, Col, Divider, Form, Input, InputNumber, message, Modal, Radio, Row, Select, Spin } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { orderBy } from 'lodash';
import { CompanySettingModel } from '@/__generated';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';

export default function GeneralTab() {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState<boolean>(false);
  React.useEffect(() => {
    setLoading(true);
    apiSdk.CompanySettingService.getSettings()
      .then(res => {
        // Réglage enregistré au format .NET (dd/MM/yyyy), proposé au format affiché (JJ/MM/AAAA).
        form.setFieldsValue({ ...res, dateFormat: toDisplayDateFormat(res.dateFormat) });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);
  const onSave = (values: CompanySettingModel) => {
    // check data
    // check range value:Range Values Cannot be Overlapping
    if (values.shipmentInsuranceSettingType === ShipmentInsuranceSettingType.RangeBase) {
      let rangeValues = values.shipmentInsuranceRangeBaseSettings || [];
      rangeValues = orderBy(rangeValues, x => x.from);
      if (rangeValues.some(x => (x as any)?.from >= (x as any)?.to)) {
        Modal.error({
          title: t('companySettingsPage_ShipmentInsuranceSettings'),
          content: t('companySettingsPage_FromLessThanTo'),
        });
        return;
      }
      if (rangeValues?.length >= 2) {
        for (var i = 0; i < rangeValues.length - 1; i++) {
          if ((rangeValues[i] as any)?.to >= (rangeValues[i + 1] as any)?.from) {
            Modal.error({
              title: t('companySettingsPage_ShipmentInsuranceSettings'),
              content: t('companySettingsPage_RangeValueCannotOverlapping'),
            });
            return;
          }
        }
      }
      values.shipmentInsuranceRangeBaseSettings = rangeValues;
    }
    setLoading(true);

    apiSdk.CompanySettingService.update({
      body: { ...values, dateFormat: toServerDateFormat(values.dateFormat) },
    })
      .then(res => {
        if (res.success) {
          applyCompanyDateFormat(values.dateFormat);
          message.success(t('companySettingsPage_UpdateSuccess'));
        } else {
          message.error(t(res.message || ''));
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const shipmentInsuranceSettingType = Form.useWatch('shipmentInsuranceSettingType', form);
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);
  return (
    <Spin spinning={loading}>
      <Form id="general-tab" onFinish={onSave} form={form} size="middle" layout="horizontal" labelAlign="right" labelCol={{ md: 10, xs: 12 }}>
        <Row gutter={ROW_GUTTER}>
          <Col xs={24} md={12} xl={12} xxl={8}>
            {/* general info */}
            <Card className="mtopo-card" size="small" title={t('companySettingsPage_CompanyGeneralInfo')}>
              <Form.Item label={t('companySettingsPage_CarrieCode')} name="carrierCode">
                <Input className="input-value-color" disabled />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_DateFormat')} name="dateFormat" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Select options={getSelectionFromArray(SupportedDateFormat)} />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_TimeFormat')} name="timeFormat" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Select options={getSelectionFromArray(SupportedTimeFormat)} />
              </Form.Item>
              {/* Langue des billets et reçus imprimés par l'app caissier (2026-11-02), indépendante de la langue de l'écran. */}
              <Form.Item
                label={t('companySettingsPage_PrintLanguage')}
                name="printLanguage"
                initialValue="fr"
                extra={t('companySettingsPage_PrintLanguageHint')}
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <Select
                  options={[
                    { value: 'fr', label: 'Français' },
                    { value: 'en', label: 'English' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label={t('companySettingsPage_InsuranceExpirationDays')}
                name="insuranceExpirationDays"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_DistanceUnit')} name="distanceUnit" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_WeightUnit')} name="weightUnit" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_IntervalDays')} name="intervalDays" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item
                label={t('companySettingsPage_TripRangeValue')}
                name="tripRangeValue"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} addonAfter={t('Mins')} className="w-100" />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_OrangeBfuSerId')} name="orangeBfuSerId">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_OrangeBfPassword')} name="orangeBfPassword">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_SmsServiceUserName')} name="smsServiceUserName">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_SmsServicePassword')} name="smsServicePassword">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_ActionCasse')} name="actionCasse">
                <Radio.Group options={ActionCasseType.__getSelectOptions()} />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_NbrVoyage')} name="nbrVoyage">
                <InputNumber min={0} className="w-100" />
              </Form.Item>

              <Form.Item label={t('companySettingsPage_TripSearchHoursGapSetup')} name="tripSearchHoursGapSetup">
                <InputNumber min={0} className="w-100" />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_TripSalesLimitTime')} name="tripSalesLimitTime">
                <InputNumber min={0} className="w-100" addonAfter={t('Mins')} />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_TripBoardingLimitTime')} name="tripBoardingLimitTime">
                <InputNumber min={0} className="w-100" addonAfter={t('Mins')} />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_CompanyLogoImage')} name="companyLogoImageUrl">
                <SingleImageUpload blobStorageContainer={BlobStorageContainerType.CompaniesContainer} />
              </Form.Item>
            </Card>
          </Col>
          <Col xs={24} md={12} xl={12} xxl={7}>
            {/* Shipment Notification Amount  */}
            <Card className="mtopo-card" title={t('companySettingsPage_ShipmentNotificationAmount')} size="small">
              <Form.Item name="shipmentNotificationTariff">
                <InputNumber min={0} className="w-100" addonAfter="FCFA" />
              </Form.Item>
              <Form.Item name="isShipmentNotificationSenderEnabled" valuePropName="checked">
                <Checkbox>{t('companySettingsPage_IsShipmentNotificationSenderEnabled')}</Checkbox>
              </Form.Item>
              <Form.Item name="isShipmentNotificationReceiverEnabled" valuePropName="checked">
                <Checkbox>{t('companySettingsPage_IsShipmentNotificationReceiverEnabled')}</Checkbox>
              </Form.Item>
            </Card>
            {/* Validity Settings   */}
            <Card className="mtopo-card" title={t('companySettingsPage_ValiditySettings')} size="small">
              <Form.Item
                label={t('companySettingsPage_TicketExpirationDays')}
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                name="ticketExpirationDays"
              >
                <InputNumber min={0} className="w-100" />
              </Form.Item>
            </Card>
            <Card className="mtopo-card" title={t('companySettingsPage_TicketModificationSetting')} size="small">
              <Form.Item
                labelCol={{ md: 14 }}
                label={t('companySettingsPage_TicketModificationLimitTime')}
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                name="ticketModificationLimitTime"
              >
                <InputNumber min={0} className="w-100" addonAfter={t('hours')} />
              </Form.Item>
            </Card>

            {/*  Book & Pay Later Settings  */}
            <Card className="mtopo-card" title={t('companySettingsPage_BookPayLaterSettings')} size="small">
              <Form.Item
                labelCol={{ md: 14 }}
                label={t('companySettingsPage_BookingAllowedTime')}
                name="bookingAllowedTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" addonAfter={t('Hours')} />
              </Form.Item>
              <Form.Item
                labelCol={{ md: 14 }}
                label={t('companySettingsPage_BookingConfirmationLimitTime')}
                name="bookingConfirmationLimitTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" addonAfter={t('Hours')} />
              </Form.Item>
              <Form.Item
                labelCol={{ md: 14 }}
                label={t('companySettingsPage_BookingReminderLimitTime')}
                name="bookingReminderLimitTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <InputNumber min={0} className="w-100" addonAfter={t('Hours')} />
              </Form.Item>
              <Form.Item
                labelCol={{ md: 14 }}
                label={t('companySettingsPage_IsSeatReleasedOnDepartureTime')}
                name="isSeatReleasedOnDepartureTime"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <Radio.Group
                  options={[
                    {
                      label: t('companySettingsPage_OnDepartureTime'),
                      value: true,
                    },
                    {
                      label: t('companySettingsPage_OnBookingTime'),
                      value: false,
                    },
                  ]}
                />
              </Form.Item>
            </Card>
            {/* sell on the go */}
            <Card className="mtopo-card" title={t('companySettingsPage_SellOnTheGoSetting')} size="small">
              <Form.Item name="isSellOnTheGoSegmentBaseTariff" valuePropName="checked">
                <Checkbox>{t('companySettingsPage_SellOnTheGoBaseTariff')}</Checkbox>
              </Form.Item>
            </Card>
          </Col>
          <Col xs={24} md={12} xl={12} xxl={9}>
            {/* Company settings detail */}
            <Card className="mtopo-card" title={t('companySettingsPage_SettingsCompanyDetails')} size="small">
              <Form.Item label={t('companySettingsPage_CompanyName')} name="name">
                <Input className="input-value-color" disabled />
              </Form.Item>

              <Form.Item label={t('base.PhoneCode')} name="phoneCode">
                <PhoneCodeInput />
              </Form.Item>
              <Form.Item label={t('base.PhoneNumber')} name="phoneNumber">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_DefaultEmail')} name="defaultEmail">
                <Input />
              </Form.Item>
              <Form.Item label={t('base.Country')} name="countryId">
                <CountryInput
                  onChange={() => {
                    form.setFieldValue('regionId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.Region')} name="regionId">
                <RegionInput
                  countryId={countryId}
                  disabled={!countryId}
                  onChange={() => {
                    form.setFieldValue('cityId', null);
                  }}
                />
              </Form.Item>
              <Form.Item label={t('base.City')} name="cityId">
                <CityInput regionId={regionId} disabled={!regionId} />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_NoCc')} name="noCc">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_NoIfu')} name="noIfu">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_EmployerNo')} name="employerNo">
                <Input />
              </Form.Item>
              <Form.Item label={t('companySettingsPage_CompanyUrl')} name="companyUrl">
                <Input />
              </Form.Item>
              <Form.Item
                label={t('companySettingsPage_TripFillUpThresholdValue')}
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                name="tripFillUpThresholdValue"
              >
                <InputNumber min={0} addonAfter="%" />
              </Form.Item>
            </Card>
            <Card className="mtopo-card" title={t('companySettingsPage_ShipmentInsuranceSettings')} size="small">
              <Form.Item
                labelCol={{ md: 16 }}
                label={t('companySettingsPage_IsShipmentMinimumRangeValue')}
                name="isShipmentMinimumRangeValue"
                valuePropName="checked"
              >
                <Checkbox />
              </Form.Item>
              <Form.Item labelCol={{ md: 8 }} label={t('companySettingsPage_ShipmentInsuranceSettingType')} name="shipmentInsuranceSettingType">
                <Radio.Group options={ShipmentInsuranceSettingType.__getSelectOptions()} />
              </Form.Item>
              {shipmentInsuranceSettingType === ShipmentInsuranceSettingType.FlatBase ? (
                <Form.Item labelCol={{ md: 8 }} label="  " colon={false} name="shipmentInsuranceFlatAmount">
                  <InputNumber min={0} addonAfter="FCFA" className="w-100" />
                </Form.Item>
              ) : null}
              {shipmentInsuranceSettingType === ShipmentInsuranceSettingType.PercentageBase ? (
                <Form.Item labelCol={{ md: 8 }} label="  " colon={false} name="shipmentInsurancePercentage">
                  <InputNumber min={0} addonBefore="%" className="w-100" />
                </Form.Item>
              ) : null}
              {shipmentInsuranceSettingType === ShipmentInsuranceSettingType.RangeBase ? (
                <Form.List name="shipmentInsuranceRangeBaseSettings">
                  {(fields, { remove, add }) => {
                    return (
                      <div>
                        <div style={{ textAlign: 'right' }}>
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            type="primary"
                            onClick={() => {
                              add();
                            }}
                          >
                            {t('companySettingsPage_AddRariff')}
                          </Button>
                        </div>
                        <Row className="components-row-header">
                          <Col md={4}>{t('base.From')}</Col>
                          <Col md={4}>{t('base.To')}</Col>
                          <Col md={4}>{t('base.Value')}</Col>
                          <Col md={10}></Col>
                          <Col md={2}></Col>
                        </Row>
                        {fields.map((field, index) => {
                          return (
                            <Row key={field.key} gutter={ROW_GUTTER}>
                              <Col md={4}>
                                <Form.Item name={[field.name, 'from']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber min={0} className="w-100" />
                                </Form.Item>
                              </Col>
                              <Col md={4}>
                                <Form.Item name={[field.name, 'to']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber min={0} className="w-100" />
                                </Form.Item>
                              </Col>
                              <Col md={4}>
                                <Form.Item name={[field.name, 'value']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber min={0} className="w-100" />
                                </Form.Item>
                              </Col>
                              <Col md={10} style={{ textAlign: 'center' }}>
                                <Form.Item name={[field.name, 'isPercentageBase']} valuePropName="checked">
                                  <Checkbox>{t('companySettingsPage_IsPercentageAmount')}</Checkbox>
                                </Form.Item>
                              </Col>
                              <Col md={2}>
                                <Button
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  danger
                                  type="link"
                                  onClick={() => {
                                    remove(index);
                                  }}
                                />
                              </Col>
                            </Row>
                          );
                        })}
                      </div>
                    );
                  }}
                </Form.List>
              ) : null}
            </Card>
          </Col>
        </Row>
        <Divider />
        <div style={{ marginBottom: 20, textAlign: 'right' }}>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            {t('save')}
          </Button>
        </div>
      </Form>
    </Spin>
  );
}
